import { all, call, put, select } from 'redux-saga/effects';

import * as actions from './users.actions';

import * as userResourceService from '../../../resource/user/user.saga-service';
import * as dialogService from '../../../dialog/dialog.saga-services';

import {
  IUserSearchParams,
  IUserCreateForm,
  IUser,
  IUpdateUserProfileData,
  IUserUpdate,
  IUserSignUpForm,
  IChangePasswordUserData,
} from '../../../resource/user/user.types';
import { clearFormAvatar } from '../../../../app/page/page-storage/users/user-form/user-form.actions';
import { usersPageSelectors } from './users.selectors';
import { userGroupSelectors } from '../../../resource/user-group/user-group.selectors';
import { IUserGroup } from '../../../resource/user-group/user-group.types';
import { storeUserGroup } from '../../../resource/user-group/user-group.actions';
import moment from 'moment';
import { formSelectors } from './user-form/user-form.selectors';
import { push } from 'connected-react-router';
import * as types from '../../../resource/user/user.types';
import { signUpUserApi } from '../../../api/request-config/signup.api';
import { apiRequest } from '../../../api/api.saga-services';
import { checkUserExistenceApi, updateUserEmailApi, enableUserApi } from '../../../api/request-config/user.api';
import { userSelectors } from '../../../resource/user/user.selectors';
import { authSelectors } from '../../../auth/auth.selectors';
import { addFlashMessage } from '../../../../utils/flashMessages.utils';
import { logIn, logOut } from '../../../auth/auth.saga-services';
import { searchUserTenants, switchTenant } from '../../../resource/user-tenants/user-tenants.saga-services';
import { ITenant } from '../../../resource/user-tenants/user-tenants.types';
import { ICheckUserExistenceResponse } from './users.types';
import { syncUserWithSlackApi } from '../../../api/request-config/slack.api';

export function* setupUsers(searchParams?: IUserSearchParams) {
  const isFetched = (yield select(usersPageSelectors.selectIsFetched)) as boolean;

  if (!isFetched) {
    const users = (yield call(userResourceService.searchUsers, searchParams)) as IUser[];

    if (users) {
      const activeUsers = users.filter((user) => !user.deleted);
      const deletedUsers = users.filter((user) => user.deleted);

      yield put(actions.storeFetchedActiveUsers(activeUsers.map((user) => user.id)));
      yield put(actions.storeFetchedDeletedUsers(deletedUsers.map((user) => user.id)));
      yield put(actions.storeIsFetched());
    } else {
      yield put(actions.clearFetchedActiveUsers());
      yield put(actions.clearFetchedDeletedUsers());
      yield put(actions.clearIsFetched());
    }
  }
}
export function* signUpUser(data: IUserSignUpForm) {
  const payload: types.IUserSignUpPayload = {
    user: {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      ...(data.password !== undefined && { password: data.password }),
      ...(data.token !== undefined && { token: data.token }),
      timezone: data.timezone,
    },
    tenant: {
      name: data.companyName,
    },
  };

  const res = (yield call(apiRequest, signUpUserApi(payload))) as { user: unknown };
  if (!res) {
    return;
  }

  yield call(logIn, undefined, res.user as types.IUserWithTokens);
  yield put(push('/login'));
}

export function* createUser(user: IUserCreateForm) {
  const loggedUserId = yield select(authSelectors.selectLoggedUser);
  const loggedUser = yield select(userSelectors.selectResourceById, loggedUserId ?? '');
  const userId = (yield call(userResourceService.createUser, {
    ...user,
    timezone: loggedUser.timezone || moment.tz.guess(),
  })) as IUser['id'];

  if (!userId) {
    return;
  }

  const activeUserIds = (yield select(usersPageSelectors.selectActiveUserIds)) as IUser['id'][];

  yield put(actions.storeFetchedActiveUsers([...activeUserIds, userId]));

  yield put(clearFormAvatar());
  yield call(addFlashMessage, { type: 'success', message: 'User has been created' });

  yield call(dialogService.closeDialog, 'newUserFormDialog');
}

export function* updateUserEmail(data: types.IUpdateUserEmailData) {
  const response = (yield call(apiRequest, updateUserEmailApi(data))) as IUser;
  if (!response) {
    return;
  }
  const loggedUserId = (yield select(authSelectors.selectLoggedUser)) as IUser['id'];
  const user = (yield select(userSelectors.selectResourceById, loggedUserId)) as IUser;
  yield call(userResourceService.storeUser, { ...user, email: response.email });
  yield call(addFlashMessage, { type: 'success', message: 'Your email has been updated.' });
}

export function* checkUserExistence(data: types.ICheckUserExistenceForm) {
  const response = (yield call(apiRequest, checkUserExistenceApi(data.email))) as ICheckUserExistenceResponse;
  if (!response) {
    return;
  }
  yield put(actions.storeUserExistenceCheck(response));
}

export function* updateUser(id: IUser['id'], user: types.IUserUpdateForm) {
  const image = (yield select(formSelectors.selectFormAvatar)) as string | undefined;
  const cropCounter = (yield select(formSelectors.selectCropCounter)) as number;
  const updatedGroupIds = user.group_ids.length > 0 ? user.group_ids.map((tuple) => tuple.value) : [];

  const updateUserData: IUserUpdate = {
    id,
    // team_ids: user.team_ids && user.team_ids.length > 0 ? user.team_ids.map((tuple) => tuple.value) : [],
    // account_ids: [], //user.account_ids.length > 0 ? user.account_ids.map((tuple) => tuple.value) : [],
    group_ids: updatedGroupIds,
    timezone: user.timezone.value,
    isAdmin: user.isAdmin ? '1' : '0',
    // cropCounter determines if there were changes in the image
    image: cropCounter > 1 ? image : undefined, // if no change, then send undefined so the API does nothing
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    jobTitle: user.jobTitle,
    address: user.address,
    alternativeAddress: user.alternativeAddress,
    phone: user.phone,
    alternativePhone: user.alternativePhone,
    dob: user.dob,
    slack: user.slack,
  };

  const userId = (yield call(userResourceService.updateUser, updateUserData)) as IUser['id'];
  if (!userId) {
    return;
  }

  // add user ID in corresponding groups
  const userGroups = (yield select(userGroupSelectors.selectResourceList)) as IUserGroup[];

  yield call(removeUserFromGroups, userGroups, updatedGroupIds, userId);
  yield call(addUserToGroups, userGroups, updatedGroupIds, userId);

  yield put(clearFormAvatar());
  yield call(dialogService.closeDialog, 'userFormDialog');
}

export function* removeUserFromGroups(
  allUserGroups: IUserGroup[],
  updatedGroupIdsForUser: IUserGroup['id'][],
  userId: IUser['id'],
) {
  const previousUserGroups = allUserGroups.filter((group) => group.userIds.includes(userId));
  const groupsToRemoveUserFrom = previousUserGroups.filter((group) => !updatedGroupIdsForUser.includes(group.id));

  yield all(
    groupsToRemoveUserFrom.map((group) => {
      const newUsersIds = group.userIds.filter((id) => id !== userId);
      return put(storeUserGroup({ ...group, userIds: newUsersIds }));
    }),
  );
}

export function* addUserToGroups(
  allUserGroups: IUserGroup[],
  updatedGroupIdsForUser: IUserGroup['id'][],
  userId: IUser['id'],
) {
  yield all(
    allUserGroups.map((group) => {
      if (updatedGroupIdsForUser.includes(group.id) && !group.userIds.includes(userId)) {
        return put(storeUserGroup({ ...group, userIds: [...group.userIds, userId] }));
      }
    }),
  );
}

export function* updateUserProfile(data: IUpdateUserProfileData) {
  yield call(userResourceService.updateUserProfile, data);
}

export function* syncUserWithSlack() {
  const response = (yield call(apiRequest, syncUserWithSlackApi())) as unknown;

  if (response) {
    yield call(addFlashMessage, {
      type: 'success',
      message: 'Users have been successfully synchronized with Slack',
    });
  }
}

export function* changePasswordUserProfile(password: IChangePasswordUserData['password']) {
  yield call(userResourceService.changePasswordUserProfile, password);
}

export function* deleteUser(userId: IUser['id']) {
  const loggedUserId = (yield select(authSelectors.selectLoggedUser)) as IUser['id'];
  const deletedUser = (yield call(userResourceService.deleteUser, userId)) as IUser | null;

  if (!deletedUser) {
    return;
  }

  if (userId === loggedUserId) {
    yield call(dialogService.closeDialog, 'deleteDialog');
    const tenants = (yield call(searchUserTenants)) as ITenant[] | undefined;

    // Self-leave deactivates the current membership. Requests with that X-Tenant-Id
    // return 401 except GET /tenants and POST /switch-tenant, so leave the dead
    // workspace: switch to another active one, or log out if none remain.
    if (tenants && tenants.length > 0) {
      yield call(switchTenant, { tenantId: tenants[0].id });
      return;
    }

    yield call(logOut);
    return;
  }

  const activeUserIds: IUser['id'][] = yield select(usersPageSelectors.selectActiveUserIds);
  const deletedUserIds: IUser['id'][] = yield select(usersPageSelectors.selectDeletedUserIds);

  // Remove from active users and add to deleted users
  yield put(actions.storeFetchedActiveUsers(activeUserIds.filter((id) => userId !== id)));
  yield put(actions.storeFetchedDeletedUsers([...deletedUserIds, userId]));

  // Update the user in the resource store to reflect the deleted: true status
  const user = (yield select(userSelectors.selectResourceById, userId)) as IUser | null;
  if (user) {
    yield call(userResourceService.storeUser, { ...user, deleted: true });
  }

  // Remove user from any user groups in the resources
  const userGroups = (yield select(userGroupSelectors.selectResourceList)) as IUserGroup[];
  yield call(removeUserFromGroups, userGroups, [], userId);

  yield call(dialogService.closeDialog, 'userFormDialog');
}

export function* enableUser(userId: IUser['id']) {
  yield call(apiRequest, enableUserApi(userId));

  const activeUserIds: IUser['id'][] = yield select(usersPageSelectors.selectActiveUserIds);
  const deletedUserIds: IUser['id'][] = yield select(usersPageSelectors.selectDeletedUserIds);

  // Remove from deleted users and add to active users
  yield put(actions.storeFetchedDeletedUsers(deletedUserIds.filter((id) => userId !== id)));
  yield put(actions.storeFetchedActiveUsers([...activeUserIds, userId]));

  // Update the user in the store to reflect the deleted: false status
  const user = (yield select(userSelectors.selectResourceById, userId)) as IUser | null;
  if (user) {
    yield call(userResourceService.storeUser, { ...user, deleted: false });
  }

  yield call(dialogService.closeDialog, 'userFormDialog');
}

export function* handleUserFromNotification(user: IUser) {
  yield call(userResourceService.populateUsers, user);

  if (user.deleted) {
    const deletedUserIds = (yield select(usersPageSelectors.selectDeletedUserIds)) as IUser['id'][];
    const updatedDeletedUserIds = deletedUserIds.includes(user.id) ? [...deletedUserIds] : [...deletedUserIds, user.id];
    yield put(actions.storeFetchedDeletedUsers(updatedDeletedUserIds));
  } else {
    const activeUserIds = (yield select(usersPageSelectors.selectActiveUserIds)) as IUser['id'][];
    const updatedActiveUserIds = activeUserIds.includes(user.id) ? [...activeUserIds] : [...activeUserIds, user.id];
    yield put(actions.storeFetchedActiveUsers(updatedActiveUserIds));
  }
}

export function* clearUsersPageStorage() {
  yield put(actions.clearFetchedActiveUsers());
  yield put(actions.clearFetchedDeletedUsers());
  yield put(actions.clearIsFetched());
}
